import { assertStaffCapability } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { checkExpectedVersion, denied, isMember, requireReason, violation, } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const importKinds = Object.freeze(["CATALOG_ITEMS"]);
export const importJobStatuses = Object.freeze([
    "UPLOADED",
    "MAPPING",
    "VALIDATING",
    "REVIEW_REQUIRED",
    "APPROVED",
    "PROCESSING",
    "COMPLETED",
    "FAILED",
]);
/** Final: a failed attempt stays as evidence, and a retry is a new attempt row. */
export const importJobTerminalStatuses = Object.freeze(["COMPLETED", "FAILED"]);
export const importFailureCodes = Object.freeze([
    "FILE_UNREADABLE",
    "LIMIT_EXCEEDED",
    "VALIDATION_ABORTED",
    "PROCESSING_ERROR",
    "REJECTED_BY_STAFF",
]);
/** Codes, not MIME strings: the enum pattern allows no `/`. The API maps a MIME type to one of these. */
export const importMediaTypes = Object.freeze(["CSV", "TSV", "XLS", "XLSX"]);
export const importRowOutcomes = Object.freeze([
    "READY",
    "WARNING",
    "INVALID",
    "DUPLICATE_CANDIDATE",
    "APPROVED",
    "IMPORTED",
    "SKIPPED",
]);
export const importDuplicateMatchBases = Object.freeze([
    "EXTERNAL_PROVIDER_ID",
    "SOURCE_ID",
    "NAME_LOCATION",
    "FUZZY_CANDIDATE",
]);
export const importApprovalActions = Object.freeze(["CREATE_NEW", "UPDATE_EXISTING"]);
export const importIssueSeverities = Object.freeze(["ERROR", "WARNING"]);
/** Issues are codes, never free text: a message is rendered from the code, and no cell text is copied. */
export const importIssueCodes = Object.freeze([
    "REQUIRED_FIELD_MISSING",
    "INVALID_FORMAT",
    "UNKNOWN_CODE",
    "OUT_OF_RANGE",
    "INVALID_TIME_ZONE",
    "INVALID_INTERVAL",
    "PRICING_INCONSISTENT",
    "DUPLICATE_IN_FILE",
    "POSSIBLE_DUPLICATE",
    "UNSUPPORTED_VALUE",
    // Phase 3.4: what normalization finds in a row that a rule cannot settle.
    "AMBIGUOUS_CURRENCY",
    "AMBIGUOUS_VALUE",
    "TIME_ZONE_INFERRED",
    "MODEL_ASSISTED",
    "DESTRUCTIVE_UPDATE",
    // CHT-1950: a Google Place ID in the file. The first two come from validation, the last two from the
    // processor (it never replaces or steals a Place ID that an item already holds).
    "PLACE_ID_INVALID",
    "MATCH_SIGNALS_DISAGREE",
    "PLACE_ID_HELD_BY_OTHER_ITEM",
    "PLACE_ID_CONFLICT",
]);
export const importJobCommands = Object.freeze([
    "start_mapping",
    "start_validation",
    "complete_validation",
    "approve",
    "start_processing",
    "complete",
    "fail",
]);
// Limits of an upload. The raw file stays in storage; only bounded raw rows are persisted.
export const IMPORT_MAX_BYTES = 10_485_760;
export const IMPORT_MAX_ROWS = 5000;
export const IMPORT_MAX_CELLS = 200;
export const IMPORT_MAX_CELL_CHARS = 2000;
/** The database CHECK bounds `octet_length(raw_cells::text)`, which is how PostgreSQL prints jsonb. */
export const IMPORT_MAX_ROW_JSON_BYTES = 16_384;
/** The last row number of a spreadsheet (Excel's limit). The database CHECK uses the same number. */
export const IMPORT_MAX_SOURCE_ROW = 1_048_576;
const rule = (id, from, to, command, who) => Object.freeze({
    id,
    from,
    to,
    command,
    staff: who.staff,
    system: Object.freeze([...(who.system ?? [])]),
});
const VALIDATOR = ["import-validator"];
const PROCESSOR = ["import-processor"];
/**
 * The twelve allowed transitions: the six steps of the happy path and a failure from each of the six
 * open states. Nothing skips a step, and COMPLETED and FAILED are final. The database guard (LF017)
 * allows exactly these (from, to) pairs.
 */
export const importJobTransitions = Object.freeze([
    rule("J1", "UPLOADED", "MAPPING", "start_mapping", { staff: true, system: VALIDATOR }),
    rule("J2", "MAPPING", "VALIDATING", "start_validation", { staff: true }),
    rule("J3", "VALIDATING", "REVIEW_REQUIRED", "complete_validation", {
        staff: false,
        system: VALIDATOR,
    }),
    rule("J4", "REVIEW_REQUIRED", "APPROVED", "approve", { staff: true }),
    rule("J5", "APPROVED", "PROCESSING", "start_processing", { staff: false, system: PROCESSOR }),
    rule("J6", "PROCESSING", "COMPLETED", "complete", { staff: false, system: PROCESSOR }),
    rule("J7", "UPLOADED", "FAILED", "fail", { staff: true, system: VALIDATOR }),
    rule("J8", "MAPPING", "FAILED", "fail", { staff: true, system: VALIDATOR }),
    rule("J9", "VALIDATING", "FAILED", "fail", { staff: true, system: VALIDATOR }),
    rule("J10", "REVIEW_REQUIRED", "FAILED", "fail", { staff: true, system: VALIDATOR }),
    rule("J11", "APPROVED", "FAILED", "fail", { staff: true, system: PROCESSOR }),
    rule("J12", "PROCESSING", "FAILED", "fail", { staff: true, system: PROCESSOR }),
]);
export function isAllowedImportJobTransition(from, to) {
    return importJobTransitions.some((row) => row.from === from && row.to === to);
}
const isTerminal = (status) => importJobTerminalStatuses.includes(status);
const SYSTEM_FAILURE_CODES = [
    "FILE_UNREADABLE",
    "LIMIT_EXCEEDED",
    "VALIDATION_ABORTED",
    "PROCESSING_ERROR",
];
/**
 * Plans one of the twelve allowed job transitions, or refuses it. Checks run in this order:
 * (1) the actor's standing (staff need `catalog.import`; a customer is `role_missing`), (2) the
 * pair (`import.terminal` from COMPLETED or FAILED, else `import.transition_invalid`), (3) the actor
 * against the rule (staff or the named system actor), (4) `expectedVersion`, (5) for a failure the
 * reason and failure code (staff reject with a reason and `REJECTED_BY_STAFF`; the system gives one of
 * the other four codes), then (6) the row evidence: validation completes when every row has an
 * outcome, approval needs every row decided and one approved, completion needs no approved row
 * left (`import.rows_undecided`). Nothing is written here.
 */
export function planImportJobTransition(input) {
    const { job, to, command, actor } = input;
    if (actor.kind !== "system")
        assertStaffCapability(actor, "catalog.import", input.environment);
    if (isTerminal(job.status))
        throw violation("import.terminal");
    const matched = importJobTransitions.find((row) => row.from === job.status && row.to === to);
    if (matched === undefined || matched.command !== command) {
        throw violation("import.transition_invalid");
    }
    if (actor.kind === "system") {
        if (!matched.system.includes(actor.actorId))
            throw denied("role_missing");
    }
    else if (!matched.staff) {
        throw denied("role_missing");
    }
    checkExpectedVersion(actor, input.expectedVersion, job.version, "import.version_conflict");
    let failureCode = null;
    if (command === "fail") {
        if (actor.kind === "staff") {
            requireReason(input.reason);
            if (input.failureCode !== "REJECTED_BY_STAFF") {
                throw new DomainValidationError("failureCode", "must be REJECTED_BY_STAFF for a staff rejection");
            }
        }
        else if (input.failureCode === null || !SYSTEM_FAILURE_CODES.includes(input.failureCode)) {
            throw new DomainValidationError("failureCode", "must be a system failure code");
        }
        failureCode = input.failureCode;
    }
    const { counts } = job;
    if (command === "complete_validation" && counts.pending > 0) {
        throw violation("import.rows_undecided");
    }
    if (command === "approve") {
        const undecided = counts.pending + counts.ready + counts.warning + counts.duplicate + counts.invalid;
        if (undecided > 0 || counts.approved < 1)
            throw violation("import.rows_undecided");
    }
    if (command === "complete" && counts.approved > 0)
        throw violation("import.rows_undecided");
    const patch = command === "approve" && actor.kind === "staff"
        ? {
            kind: "set_status",
            status: to,
            approvedByStaffUserId: actor.authority.staffUserId,
            approvedAt: "now",
        }
        : command === "complete"
            ? { kind: "set_status", status: to, completedAt: "now" }
            : command === "fail" && failureCode !== null
                ? { kind: "set_status", status: to, failureCode, failedAt: "now" }
                : { kind: "set_status", status: to };
    const auditAction = {
        start_mapping: "import_job.mapping_started",
        start_validation: "import_job.validation_started",
        complete_validation: "import_job.review_required",
        approve: "import_job.approved",
        start_processing: "import_job.processing_started",
        complete: "import_job.completed",
        fail: actor.kind === "staff" ? "import_job.rejected_by_staff" : "import_job.failed",
    }[command];
    return Object.freeze({ ruleId: matched.id, command, from: job.status, to, patch, auditAction });
}
const rowRule = (id, from, to, actor, jobStatus) => Object.freeze({ id, from, to, actor, jobStatus });
/**
 * The thirteen allowed outcome changes (eight from-values, NULL included, by seven outcomes). The
 * validator decides the first outcome while the job is VALIDATING; a catalog editor decides in
 * REVIEW_REQUIRED; the processor imports in PROCESSING. INVALID can only be SKIPPED: invalid
 * ambiguity is never imported. The database guard (LF017) allows exactly these pairs.
 */
export const importRowTransitions = Object.freeze([
    rowRule("O1", null, "READY", "validator", "VALIDATING"),
    rowRule("O2", null, "WARNING", "validator", "VALIDATING"),
    rowRule("O3", null, "INVALID", "validator", "VALIDATING"),
    rowRule("O4", null, "DUPLICATE_CANDIDATE", "validator", "VALIDATING"),
    rowRule("O5", "READY", "APPROVED", "staff", "REVIEW_REQUIRED"),
    rowRule("O6", "READY", "SKIPPED", "staff", "REVIEW_REQUIRED"),
    rowRule("O7", "WARNING", "APPROVED", "staff", "REVIEW_REQUIRED"),
    rowRule("O8", "WARNING", "SKIPPED", "staff", "REVIEW_REQUIRED"),
    rowRule("O9", "DUPLICATE_CANDIDATE", "APPROVED", "staff", "REVIEW_REQUIRED"),
    rowRule("O10", "DUPLICATE_CANDIDATE", "SKIPPED", "staff", "REVIEW_REQUIRED"),
    rowRule("O11", "INVALID", "SKIPPED", "staff", "REVIEW_REQUIRED"),
    rowRule("O12", "APPROVED", "IMPORTED", "processor", "PROCESSING"),
    rowRule("O13", "APPROVED", "SKIPPED", "staff", "REVIEW_REQUIRED"),
]);
export function isAllowedImportRowTransition(from, to) {
    return importRowTransitions.some((row) => row.from === from && row.to === to);
}
const fail = (field, rule) => new DomainValidationError(field, rule);
function assertKnownIssues(issues) {
    for (const issue of issues) {
        if (!isMember(importIssueSeverities, issue.severity))
            throw fail("issues", "has an unknown severity");
        if (!isMember(importIssueCodes, issue.code))
            throw fail("issues", "has an unknown code");
    }
}
/**
 * Plans one outcome change of an import row, or refuses it. Checks run in this order: (1) staff
 * standing (`catalog.import`), (2) the pair (`import.row_transition_invalid`), (3) the job phase the
 * pair belongs to (the same code), (4) the actor against the rule (the validator, a catalog editor or
 * the processor), then (5) the evidence: READY has no issues, WARNING a warning and no error,
 * INVALID an error, DUPLICATE_CANDIDATE a candidate and no error; an approval names its action
 * (`CREATE_NEW`, or `UPDATE_EXISTING` for a duplicate candidate); an import names the item it made.
 */
export function planImportRowOutcome(input) {
    const { row, to, actor, evidence } = input;
    if (actor.kind !== "system")
        assertStaffCapability(actor, "catalog.import", input.environment);
    const matched = importRowTransitions.find((candidate) => candidate.from === row.outcome && candidate.to === to);
    if (matched === undefined || matched.jobStatus !== input.jobStatus) {
        throw violation("import.row_transition_invalid");
    }
    switch (matched.actor) {
        case "validator":
        case "processor": {
            const required = matched.actor === "validator" ? "import-validator" : "import-processor";
            if (actor.kind !== "system" || actor.actorId !== required)
                throw denied("role_missing");
            break;
        }
        case "staff":
            if (actor.kind !== "staff")
                throw denied("role_missing");
            break;
    }
    assertKnownIssues(evidence.issues);
    const errors = evidence.issues.filter((issue) => issue.severity === "ERROR").length;
    const warnings = evidence.issues.length - errors;
    const patch = {
        kind: "record_outcome",
        outcome: to,
        outcomeRecordedAt: "now",
    };
    switch (to) {
        case "READY":
            if (evidence.issues.length > 0)
                throw fail("issues", "a ready row has no issues");
            break;
        case "WARNING":
            if (warnings < 1 || errors > 0)
                throw fail("issues", "a warning row has a warning and no error");
            break;
        case "INVALID":
            if (errors < 1)
                throw fail("issues", "an invalid row has an error");
            break;
        case "DUPLICATE_CANDIDATE": {
            const duplicate = evidence.duplicate;
            if (duplicate === null || !isMember(importDuplicateMatchBases, duplicate.basis)) {
                throw fail("duplicate", "a duplicate candidate names the item and the match basis");
            }
            if (errors > 0)
                throw fail("issues", "a duplicate candidate has no error");
            patch.duplicateOfCatalogItemId = duplicate.catalogItemId;
            patch.duplicateMatchBasis = duplicate.basis;
            break;
        }
        case "APPROVED": {
            const action = evidence.approvalAction;
            if (action === null || !isMember(importApprovalActions, action)) {
                throw fail("approvalAction", "an approval names its action");
            }
            if (row.outcome !== "DUPLICATE_CANDIDATE" && action !== "CREATE_NEW") {
                throw fail("approvalAction", "only a duplicate candidate may update an existing item");
            }
            if (action === "UPDATE_EXISTING" && row.duplicateOfCatalogItemId === null) {
                throw fail("approvalAction", "updating an existing item needs a candidate");
            }
            patch.approvalAction = action;
            if (action === "UPDATE_EXISTING" && input.chosenCandidate !== undefined) {
                patch.duplicateOfCatalogItemId = input.chosenCandidate.catalogItemId;
                patch.duplicateMatchBasis = input.chosenCandidate.basis;
            }
            break;
        }
        case "IMPORTED":
            if (evidence.resultingCatalogItemId === null) {
                throw fail("resultingCatalogItemId", "an import names the item it made");
            }
            patch.resultingCatalogItemId = evidence.resultingCatalogItemId;
            break;
        case "SKIPPED":
            break;
    }
    if (matched.actor === "staff" && actor.kind === "staff") {
        patch.reviewedByStaffUserId = actor.authority.staffUserId;
        patch.reviewedAt = "now";
    }
    return Object.freeze({
        ruleId: matched.id,
        from: row.outcome,
        to,
        patch: Object.freeze(patch),
        issues: matched.actor === "validator" ? evidence.issues : [],
        auditAction: matched.actor === "staff" ? "import_rows.reviewed" : null,
    });
}
const SHA256 = /^[0-9a-f]{64}$/;
const OBJECT_PATH = /^[A-Za-z0-9/_.-]{1,512}$/;
// A lone surrogate is not valid UTF-8, so PostgreSQL would reject it in jsonb.
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
/**
 * The size of a row's raw cells as PostgreSQL prints them (`raw_cells::text`): a JSON array whose
 * elements are separated by a comma and a space. The database CHECK bounds this number, so the
 * domain measures the same thing.
 */
export function rawCellsJsonBytes(cells) {
    return Buffer.byteLength(`[${cells.map((cell) => JSON.stringify(cell)).join(", ")}]`, "utf8");
}
/**
 * Validates an upload before anything is stored. The raw rows are untrusted text: they are kept
 * verbatim (a formula or a URL is just text, never evaluated, fetched or trimmed), bounded in count,
 * length and size, and never echoed in an error. File name and path are display and storage
 * labels only. Only bounded raw rows are persisted; the file itself stays in storage.
 */
export function validateImportUpload(input) {
    if (!isMember(importMediaTypes, input.mediaType))
        throw fail("mediaType", "must be CSV, TSV, XLS or XLSX");
    if (typeof input.byteSize !== "number" ||
        !Number.isInteger(input.byteSize) ||
        input.byteSize < 1 ||
        input.byteSize > IMPORT_MAX_BYTES) {
        throw fail("byteSize", `must be a whole number from 1 to ${IMPORT_MAX_BYTES}`);
    }
    if (typeof input.sha256 !== "string" || !SHA256.test(input.sha256)) {
        throw fail("sha256", "must be 64 lowercase hexadecimal characters");
    }
    if (typeof input.objectPath !== "string" || !OBJECT_PATH.test(input.objectPath)) {
        throw fail("objectPath", "must be a storage path of 1 to 512 safe characters");
    }
    const fileName = boundedText("fileName", input.fileName, 255);
    if (!Array.isArray(input.rows) || input.rows.length > IMPORT_MAX_ROWS) {
        throw fail("rows", `must hold at most ${IMPORT_MAX_ROWS} rows`);
    }
    const numbered = input.rows.filter((row) => row?.sourceRowNumber !== undefined).length;
    if (numbered !== 0 && numbered !== input.rows.length) {
        throw fail("rows.sourceRowNumber", "must be given for every row or for none");
    }
    let previousSourceRow = 0;
    const rows = input.rows.map((row, index) => {
        const cells = row?.cells;
        if (!Array.isArray(cells) || cells.length > IMPORT_MAX_CELLS) {
            throw fail("rows.cells", `must be a list of at most ${IMPORT_MAX_CELLS} cells`);
        }
        for (const cell of cells) {
            if (typeof cell !== "string")
                throw fail("rows.cells", "must be text");
            if (cell.includes("\u0000") || LONE_SURROGATE.test(cell)) {
                throw fail("rows.cells", "must not contain NUL or unpaired surrogates");
            }
            if ([...cell].length > IMPORT_MAX_CELL_CHARS) {
                throw fail("rows.cells", `must be at most ${IMPORT_MAX_CELL_CHARS} characters`);
            }
        }
        if (rawCellsJsonBytes(cells) > IMPORT_MAX_ROW_JSON_BYTES) {
            throw fail("rows.cells", `must fit in ${IMPORT_MAX_ROW_JSON_BYTES} bytes as stored`);
        }
        let sourceRowNumber = null;
        if (row.sourceRowNumber !== undefined) {
            const given = row.sourceRowNumber;
            if (typeof given !== "number" ||
                !Number.isInteger(given) ||
                given <= previousSourceRow ||
                given > IMPORT_MAX_SOURCE_ROW) {
                throw fail("rows.sourceRowNumber", `must be whole numbers that increase, from 1 to ${IMPORT_MAX_SOURCE_ROW}`);
            }
            previousSourceRow = given;
            sourceRowNumber = given;
        }
        return { rowNumber: index + 1, rawCells: Object.freeze([...cells]), sourceRowNumber };
    });
    return Object.freeze({
        fileName,
        mediaType: input.mediaType,
        byteSize: input.byteSize,
        sha256: input.sha256,
        objectPath: input.objectPath,
        rowCount: rows.length,
        rows: Object.freeze(rows),
    });
}
export const IMPORT_MAX_ATTEMPTS = 50;
/**
 * A retry is a new attempt, never a reopened job: only a FAILED job may be retried (a catalog
 * editor's command), the new job links the failed one and carries a fresh upload, and the failure
 * stays as evidence.
 */
export function planImportRetry(input) {
    assertStaffCapability(input.actor, "catalog.import", input.environment);
    if (input.job.status !== "FAILED" || input.job.attemptNumber >= IMPORT_MAX_ATTEMPTS) {
        throw violation("import.retry_not_allowed");
    }
    return Object.freeze({
        attemptNumber: input.job.attemptNumber + 1,
        retryOfImportJobId: input.job.id,
        upload: validateImportUpload(input.upload),
        auditAction: "import_job.retry_created",
    });
}
